import _ from 'lodash';

import type {
  Coverage,
  GetPresetsResponse,
} from '../../../../../../api/rater.api';
import { ProductTypes, type ProductType } from '../../../../../../types';
import type { UpdateQuoteCartListPayload } from '../../../../../../api/quotes.api.types';
import type { CoverageState, CustomizeQuoteState } from '../reducer/types';
import { toUniversalDate } from '../../../../../../utils/date.utils';
import { getAmendatoryMetaData } from '../../utils/primeX.util';

export const getFormattedCoveragesFromState = (
  coverages: CustomizeQuoteState['coverages'],
  globalRetroActivePeriod: number | string
) => {
  const getRetroFromCoverage = (coverage: CoverageState) => {
    if (coverage.rules?.lockRetrosToGlobalOnSubmit) {
      return {
        retroActivePeriod: globalRetroActivePeriod,
      };
    }

    if (getIsRetroDate(coverage.retroPeriod)) {
      return {
        retroActiveDate: coverage.retroPeriod,
      };
    }

    return {
      retroActivePeriod: coverage.retroPeriod,
    };
  };

  return Object.keys(coverages || {})
    .map((coverageName) => {
      return {
        name: coverages[coverageName].name,
        uuid: coverages[coverageName].uuid,
        deductible: coverages[coverageName].deductible,
        limit: coverages[coverageName].limit,
        waitingPeriod: coverages[coverageName].waitingPeriod,
        isAvailable: coverages[coverageName].isAvailable,
        isSelected: coverages[coverageName].isSelected,
        ...getAmendatoryMetaData(coverages[coverageName]),
        ...getRetroFromCoverage(coverages[coverageName]),
      };
    })
    .filter((coverage) => coverage.isAvailable && coverage.isSelected)
    .map((filteredCoverage) =>
      _.omit(filteredCoverage, ['isAvailable', 'isSelected'])
    );
};

export const sortCoverages = (presetResponse: GetPresetsResponse) => {
  const presetResponseClone = _.cloneDeep(presetResponse);

  Object.keys(presetResponse.coverageGroups || {}).forEach((coverageGroup) => {
    const mandatoryCoverages = [] as unknown as Coverage[];
    const selectedCoverages = [] as unknown as Coverage[];
    const availableCoverages = [] as unknown as Coverage[];
    const notAvailableCoverages = [] as unknown as Coverage[];

    presetResponse.coverageGroups[coverageGroup].forEach((coverage) => {
      if (coverage.rules.isMandatory) {
        return mandatoryCoverages.push(coverage);
      }

      if (coverage.preset.isSelected && !coverage.rules.isMandatory) {
        return selectedCoverages.push(coverage);
      }

      if (!coverage.preset.isSelected && coverage.preset.isAvailable) {
        return availableCoverages.push(coverage);
      }

      return notAvailableCoverages.push(coverage);
    });

    const sortedCoverages = [
      ...mandatoryCoverages,
      ...selectedCoverages,
      ...availableCoverages,
      ...notAvailableCoverages,
    ];

    presetResponseClone.coverageGroups[coverageGroup] = sortedCoverages;
  });

  return presetResponseClone as GetPresetsResponse;
};

export const getCartPayload = (
  productType: ProductType,
  payload: UpdateQuoteCartListPayload
) => {
  if (productType === ProductTypes.prime_one) {
    if (getIsRetroDate(payload.retroactivePeriod)) {
      const sanitizedPayload = _.omit(payload, ['retroactivePeriod']);
      return {
        ...sanitizedPayload,
        retroActiveDate: payload.retroactivePeriod,
      };
    }
    return payload;
  }

  return _.omit(payload, ['commissionRate']);
};

interface HandleUpdateCoveragesParams {
  state: CustomizeQuoteState;
  globalRetro: number | string;
  isRetroDate?: boolean;
}

export const handleUpdateCoveragesRetro = ({
  state,
  globalRetro,
  isRetroDate = false,
}: HandleUpdateCoveragesParams) => {
  if (state.productType === ProductTypes.p100_pro) {
    return state.coverages;
  }

  return Object.keys(state.coverages).reduce((acc, coverage) => {
    if (
      !acc[coverage]?.retroPeriod ||
      !acc[coverage].ui_retroPeriod_options.length ||
      (!acc[coverage].hasCustomRetroDate && isRetroDate)
    ) {
      return acc;
    }

    if (isRetroDate) {
      return {
        ...acc,
        [coverage]: {
          ...acc[coverage],
          retroPeriod: globalRetro,
          ui_retroPeriod_options: [
            ...acc[coverage].ui_retroPeriod_options,
            { value: globalRetro, label: toUniversalDate(globalRetro) },
          ],
        },
      };
    }

    const coverageRetroOption = acc[coverage].ui_retroPeriod_options.find(
      (option: { label: string; value: number | string }) =>
        `${option.value}` === `${globalRetro}`
    );

    if (!coverageRetroOption) {
      return acc;
    }

    return {
      ...acc,
      [coverage]: {
        ...acc[coverage],
        retroPeriod: globalRetro,
      },
    };
  }, state.coverages);
};

interface UpdateCoveragesWaitingPeriodParams {
  state: CustomizeQuoteState;
  globalWaitingPeriod: number;
}

export const handleUpdateCoveragesWaitingPeriod = ({
  state,
  globalWaitingPeriod,
}: UpdateCoveragesWaitingPeriodParams) => {
  if (state.productType === ProductTypes.p100_pro) {
    return state.coverages;
  }

  return Object.keys(state.coverages).reduce((acc, coverage) => {
    if (
      !acc[coverage].waitingPeriod ||
      !acc[coverage].ui_waitingPeriod_options.length
    ) {
      return acc;
    }

    const deriveWaitingPeriod = () => {
      const options = acc[coverage].ui_waitingPeriod_options;

      if (globalWaitingPeriod > options[options.length - 1].value) {
        return options[options.length - 1].value;
      }

      if (globalWaitingPeriod < options[0].value) {
        return options[0].value;
      }

      return globalWaitingPeriod;
    };

    return {
      ...acc,
      [coverage]: {
        ...acc[coverage],
        waitingPeriod: deriveWaitingPeriod(),
      },
    };
  }, state.coverages);
};

export const RETRO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export const getIsRetroDate = (retro: CoverageState['retroPeriod']) => {
  if (!retro) {
    return false;
  }
  const retroString = String(retro);
  return RETRO_DATE_REGEX.test(retroString);
};

interface UpdateDeductibleParams {
  state: CustomizeQuoteState;
  globalDeductible: number;
}

export const handleUpdateCoverageDeductibles = ({
  state,
  globalDeductible,
}: UpdateDeductibleParams) => {
  if (state.productType === ProductTypes.p100_pro) {
    return state.coverages;
  }

  return Object.keys(state.coverages).reduce((acc, coverage) => {
    if (
      !acc[coverage].deductible ||
      !acc[coverage].ui_deductible_options.length
    ) {
      return acc;
    }

    const deriveDeductible = () => {
      const options = acc[coverage].ui_deductible_options;

      if (globalDeductible > options[options.length - 1].value) {
        return options[options.length - 1].value;
      }

      if (globalDeductible < options[0].value) {
        return options[0].value;
      }

      return globalDeductible;
    };

    return {
      ...acc,
      [coverage]: {
        ...acc[coverage],
        deductible: deriveDeductible(),
      },
    };
  }, state.coverages);
};
